import React, { useRef, useState, useSyncExternalStore } from 'react';
import {
  AlertTriangle, Archive, BookOpenCheck, Check, CheckCircle2, Eye, FlaskConical, Info, Loader2, PencilLine, Plus, RefreshCw, RotateCcw, Search, ShieldCheck, Trash2, Users, X,
} from 'lucide-react';
import type { SifaUnterweisungVorlage, UnterweisungStatus } from '../../../services/sifa/types';
import {
  VorlageKonfliktError, createVorlage, deleteVorlageEntwurf, setzeVorlagenStatus, vorlageLoeschbarHinweis,
} from '../../../services/sifa/vorlagenService';
import { vorlageMedienLoeschbar } from '../../../services/sifa/vorlagenLogic';
import { rechtFehlerOder } from '../../../services/rechte/rechtFehler';
import { formatDatum, gueltigkeitText, pruefeEntwurf } from '../unterweisungModulLogic';
import { VorlagenEditor } from '../UnterweisungEditor';
import { TestLinkDialog } from '../TestLinkDialog';
import { TEST_TOOLTIP } from '../testLinkLogic';
import { DatenschutzReiter } from './DatenschutzReiter';
import { RueckfrageDialog } from './RueckfrageDialog';
import { useVorlagen } from './useVorlagen';
import { abonniereMeineRechte, meineRechteStand } from '../../../services/rechte/rechteService';
import {
  FRAGE_ARCHIVIEREN,
  FRAGE_ERSTE_FREIGABE,
  FREIGEGEBEN_MELDUNG,
  KEIN_RECHT_HINWEIS,
  OHNE_KATEGORIE,
  STATUS_FILTER_LABEL,
  STATUS_FILTER_VORGABE,
  VORLAGEN_STATUS_LABEL,
  VorlagenStatusFilter,
  ZeilenAktion,
  darfVorlagenBearbeiten,
  entwurfAusVorlage,
  filtereVorlagen,
  frageLoeschen,
  gruppiereNachKategorie,
  kategorienAus,
  kopienText,
  zaehleStatus,
  zeilenAktionen,
} from './vorlagenViewLogic';

interface Props {
  /** Admin? Bearbeiten der Vorlagen und — nur Admins — der Reiter Datenschutzhinweise. */
  isAdmin: boolean;
  /** Haekchen „SiFa" — kommt nach dem Start verspaetet (istSifaFachkraft), daher je Render ausgewertet. */
  istSifa: boolean;
  isMobile?: boolean;
  /** Eigener Firmenname (Profil) — Beispiel-Auftragsverarbeiter in der Datenschutz-Vorschau. */
  bueroName?: string | null;
}

type ListenFrage = { art: 'freigeben' | 'archivieren' | 'loeschen'; vorlage: SifaUnterweisungVorlage };

type Reiter = 'vorlagen' | 'datenschutz';

const REITER: { key: Reiter; label: string; icon: React.ReactNode }[] = [
  { key: 'vorlagen', label: 'Vorlagen', icon: <BookOpenCheck className="w-4 h-4" /> },
  { key: 'datenschutz', label: 'Datenschutzhinweise', icon: <ShieldCheck className="w-4 h-4" /> },
];

const titelVon = (v: Pick<SifaUnterweisungVorlage, 'titel'>): string => (v.titel || '').trim() || 'ohne Titel';

/**
 * Ansicht „Unterweisungen": die globalen Vorlagen der Firma (Nr. 141,
 * docs/sifa-vorlagen-plan.md §3e) — duenner Container wie UnterweisungModul.
 *
 * Liste mit Suche, Statusfilter und Gruppen nach Kategorie; Bearbeiten im
 * selben Editor wie beim Kunden (VorlagenEditor). Schreiben duerfen Admins,
 * Nutzer mit Haekchen „SiFa" und wer das Recht „SiFa-Vorlagen" hat — das
 * prueft hier nur die Oberflaeche, der Riegel ist die RLS-Policy. Jede Regel
 * (Filter, Gruppen, welche Aktion je Zeile) steht in vorlagenViewLogic.ts.
 *
 * Zweiter Reiter: die Datenschutzhinweise der Teilnehmerseite
 * (DatenschutzReiter, Pflege nur Admins, docs/sifa-ausbau2-plan.md §0.4).
 */
export default function VorlagenView({ isAdmin, istSifa, isMobile = false, bueroName }: Props) {
  // Recht „SiFa-Vorlagen" (Rollensystem) aus der Antwort der Datenbank
  // (`meine_rechte()`, Sitzungsspeicher wie useRechte) — ODER neben Admin und
  // Haekchen. Ohne Antwort (Migration offen) zaehlen wie bisher nur diese zwei.
  const rechteStand = useSyncExternalStore(abonniereMeineRechte, meineRechteStand, meineRechteStand);
  const darfBearbeiten = darfVorlagenBearbeiten(isAdmin, istSifa, rechteStand.dbRechte?.has('sifa.vorlagen') ?? false);
  const { vorlagen, kopien, laden, fehler, neuLaden, uebernehmen, entfernen } = useVorlagen();

  const [suche, setSuche] = useState('');
  const [statusFilter, setStatusFilter] = useState<VorlagenStatusFilter>(STATUS_FILTER_VORGABE);
  const [offenId, setOffenId] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [anlegenLaeuft, setAnlegenLaeuft] = useState(false);
  const [anlegenFehler, setAnlegenFehler] = useState<string | null>(null);
  const [frage, setFrage] = useState<ListenFrage | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<{ text: string; ton: 'info' | 'fehler' } | null>(null);
  const [testFuer, setTestFuer] = useState<SifaUnterweisungVorlage | null>(null);
  const [reiter, setReiter] = useState<Reiter>('vorlagen');
  /** Ungespeichertes im Reiter „Datenschutzhinweise" — vor dem Wechsel nachfragen. */
  const [datenschutzDirty, setDatenschutzDirty] = useState(false);
  const [reiterFrage, setReiterFrage] = useState<Reiter | null>(null);
  /** Riegel gegen den zweiten Klick — der Zustand steht erst nach dem naechsten Zeichnen. */
  const laeuftRef = useRef(false);

  const kategorien = kategorienAus(vorlagen);
  const offene = offenId ? vorlagen.find((v) => v.id === offenId) ?? null : null;

  const zeige = (text: string, ton: 'info' | 'fehler' = 'info') => setMeldung({ text, ton });

  /** Der Hinweis des Service entscheidet, ob „Loeschen" angeboten wird (Kundenzahl aus zaehleKundenJeVorlage). */
  const loeschHinweis = (v: SifaUnterweisungVorlage): string | null => vorlageLoeschbarHinweis(v, kopien.get(v.id) ?? 0);

  const reiterWechseln = (ziel: Reiter) => {
    if (ziel === reiter) return;
    if (reiter === 'datenschutz' && datenschutzDirty) { setReiterFrage(ziel); return; }
    setDatenschutzDirty(false);
    setReiter(ziel);
  };

  // ─── Anlegen ──────────────────────────────────────────────────────────────

  const anlegen = async (titel: string, kategorie: string) => {
    if (!darfBearbeiten || laeuftRef.current) return;
    laeuftRef.current = true;
    setAnlegenLaeuft(true);
    setAnlegenFehler(null);
    try {
      const neu = await createVorlage({ titel, kategorie });
      uebernehmen(neu);
      setNeuOffen(false);
      setMeldung(null);
      setOffenId(neu.id);
    } catch (e) {
      setAnlegenFehler(rechtFehlerOder(e));
    } finally {
      laeuftRef.current = false;
      setAnlegenLaeuft(false);
    }
  };

  // ─── Status / Loeschen ────────────────────────────────────────────────────

  const statusSetzen = async (v: SifaUnterweisungVorlage, status: UnterweisungStatus) => {
    if (!darfBearbeiten || laeuftRef.current) return;
    laeuftRef.current = true;
    setBusyId(v.id);
    setMeldung(null);
    try {
      // Gebunden an Fassung und Status dieser Listenzeile: geprueft
      // (pruefeEntwurf) und gesehen wurde genau dieser Stand — nicht ein
      // spaeter gespeicherter und nicht einer, den ein anderer inzwischen
      // archiviert oder freigegeben hat.
      const neu = await setzeVorlagenStatus(v.id, status, v.fassung, v.status);
      uebernehmen(neu);
      zeige(status === 'aktiv'
        ? FREIGEGEBEN_MELDUNG
        : status === 'archiviert'
          ? `„${titelVon(v)}" ist archiviert. Bestehende Kundenkopien laufen weiter.`
          : `„${titelVon(v)}" ist wieder ein Entwurf.`);
    } catch (e) {
      if (e instanceof VorlageKonfliktError) {
        zeige(`„${titelVon(v)}" wurde inzwischen von jemand anderem geändert — der Status blieb unverändert. Die Liste wird neu geladen; bitte den neuen Stand prüfen.`, 'fehler');
        void neuLaden();
      } else {
        zeige(rechtFehlerOder(e), 'fehler');
      }
    } finally {
      laeuftRef.current = false;
      setBusyId(null);
      setFrage(null);
    }
  };

  /** Prueft wie der Editor (pruefeEntwurf); nur vor der ERSTEN Freigabe eine Rueckfrage. */
  const freigeben = (v: SifaUnterweisungVorlage) => {
    const maengel = pruefeEntwurf(entwurfAusVorlage(v));
    if (maengel.length > 0) {
      zeige(`„${titelVon(v)}" lässt sich noch nicht freigeben: ${maengel.join(' ')} Bitte im Editor ergänzen.`, 'fehler');
      return;
    }
    if (vorlageMedienLoeschbar(v)) setFrage({ art: 'freigeben', vorlage: v });
    else void statusSetzen(v, 'aktiv');
  };

  const loeschen = async (v: SifaUnterweisungVorlage) => {
    if (!darfBearbeiten || laeuftRef.current) return;
    laeuftRef.current = true;
    setBusyId(v.id);
    setMeldung(null);
    try {
      if (await deleteVorlageEntwurf(v.id)) {
        entfernen(v.id);
        zeige(`Vorlage „${titelVon(v)}" gelöscht.`);
      } else {
        zeige(`„${titelVon(v)}" gibt es nicht mehr. Die Liste wird neu geladen.`, 'fehler');
        void neuLaden();
      }
    } catch (e) {
      // Die Datenbank hat abgelehnt (in Verwendung, inzwischen freigegeben …) —
      // Klartext vom Service, und die Liste zeigt danach den echten Stand.
      zeige(`${rechtFehlerOder(e)} Die Liste wird neu geladen.`, 'fehler');
      void neuLaden();
    } finally {
      laeuftRef.current = false;
      setBusyId(null);
      setFrage(null);
    }
  };

  const frageBestaetigt = (f: ListenFrage) => {
    if (f.art === 'loeschen') void loeschen(f.vorlage);
    else void statusSetzen(f.vorlage, f.art === 'freigeben' ? 'aktiv' : 'archiviert');
  };

  // ─── Editor ───────────────────────────────────────────────────────────────

  if (offene) {
    return (
      <VorlagenEditor
        key={offene.id}
        vorlage={offene}
        isReadOnly={!darfBearbeiten}
        isMobile={isMobile}
        kategorien={kategorien}
        onZurueck={() => setOffenId(null)}
        onGespeichert={uebernehmen}
      />
    );
  }

  // ─── Liste ────────────────────────────────────────────────────────────────

  const zaehler = zaehleStatus(vorlagen);
  const sichtbar = filtereVorlagen(vorlagen, { suche, status: statusFilter });
  const gruppen = gruppiereNachKategorie(sichtbar);
  const aktion = 'min-h-[40px] px-3 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40';

  const statusChip = (v: SifaUnterweisungVorlage) => {
    const ton = v.status === 'aktiv'
      ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
      : v.status === 'archiviert'
        ? 'text-slate-600 bg-slate-100 border-slate-200'
        : 'text-amber-700 bg-amber-50 border-amber-200';
    const Icon = v.status === 'aktiv' ? Check : v.status === 'archiviert' ? Archive : PencilLine;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold border rounded-full ${ton}`}>
        <Icon className="w-3 h-3" /> {VORLAGEN_STATUS_LABEL[v.status]}
      </span>
    );
  };

  const knopf = (v: SifaUnterweisungVorlage, a: ZeilenAktion) => {
    const gesperrt = busyId !== null;
    const laeuft = busyId === v.id;
    switch (a) {
      case 'bearbeiten':
      case 'ansehen':
        return (
          <button key={a} onClick={() => setOffenId(v.id)} className={`${aktion} text-blue-700 bg-blue-50 hover:bg-blue-100`}>
            {a === 'bearbeiten' ? <PencilLine className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {a === 'bearbeiten' ? 'Bearbeiten' : 'Ansehen'}
          </button>
        );
      case 'testen':
        return (
          <button key={a} onClick={() => setTestFuer(v)} className={`${aktion} text-teal-700 bg-teal-50 hover:bg-teal-100`} title={TEST_TOOLTIP}>
            <FlaskConical className="w-3.5 h-3.5" /> Testen
          </button>
        );
      case 'freigeben':
      case 'wiederFreigeben':
        return (
          <button key={a} onClick={() => freigeben(v)} disabled={gesperrt} className={`${aktion} text-emerald-700 bg-emerald-50 hover:bg-emerald-100`}>
            {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : a === 'freigeben' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />}
            {a === 'freigeben' ? 'Freigeben' : 'Wieder freigeben'}
          </button>
        );
      case 'archivieren':
        return (
          <button key={a} onClick={() => setFrage({ art: 'archivieren', vorlage: v })} disabled={gesperrt} className={`${aktion} text-slate-700 bg-slate-100 hover:bg-slate-200`}>
            <Archive className="w-3.5 h-3.5" /> Archivieren
          </button>
        );
      case 'loeschen':
        return (
          <button key={a} onClick={() => setFrage({ art: 'loeschen', vorlage: v })} disabled={gesperrt} className={`${aktion} text-red-600 hover:bg-red-50`} title="Nur Entwürfe, auf die nichts mehr verweist — das prüft die Datenbank beim Löschen">
            <Trash2 className="w-3.5 h-3.5" /> Löschen
          </button>
        );
    }
  };

  const karte = (v: SifaUnterweisungVorlage) => {
    const hatVideo = v.inhalt.some((a) => !!a.videoPfad);
    const wer = (v.geaendertVonName || '').trim();
    const hinweis = loeschHinweis(v);
    return (
      <div key={v.id} className={`bg-white border border-slate-200 rounded-2xl p-4 space-y-3 ${v.status === 'archiviert' ? 'opacity-80' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">{titelVon(v)}</p>
            <p className="text-xs text-slate-500 truncate">{(v.kategorie || '').trim() || OHNE_KATEGORIE}</p>
            {v.beschreibung && <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{v.beschreibung}</p>}
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1">
            {statusChip(v)}
            <span className="text-[11px] font-semibold text-slate-500">Fassung {v.fassung}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1 font-semibold text-slate-600" title="Zahl der Kundenkopien dieser Vorlage">
            <Users className="w-3 h-3" /> {kopienText(kopien.get(v.id) ?? 0)}
          </span>
          <span>{v.inhalt.length} Abschnitte, {v.fragen.length} Fragen{hatVideo ? ', mit Video' : ''}</span>
          <span>Gültigkeit {gueltigkeitText(v.gueltigMonate)}{v.dauerMin ? ` · ca. ${v.dauerMin} min` : ''}</span>
          <span>geändert {formatDatum(v.updatedAt)}{wer ? ` von ${wer}` : ''}</span>
        </div>
        {darfBearbeiten && v.status === 'entwurf' && hinweis && (
          <p className="text-[11px] text-amber-800 flex items-start gap-1"><Info className="w-3 h-3 shrink-0 mt-0.5" /> {hinweis}</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {zeilenAktionen(v, darfBearbeiten, hinweis).map((a) => knopf(v, a))}
        </div>
      </div>
    );
  };

  const frageText = frage
    ? frage.art === 'loeschen' ? frageLoeschen(frage.vorlage) : frage.art === 'freigeben' ? FRAGE_ERSTE_FREIGABE : FRAGE_ARCHIVIEREN
    : '';

  return (
    <div data-glass-view="sifa" className="h-full overflow-y-auto bg-slate-50">
      <div className={`max-w-6xl mx-auto p-4 md:p-6 space-y-4 ${isMobile ? 'pb-20' : 'pb-10'}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 shrink-0 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center">
              <BookOpenCheck className="w-5 h-5 text-teal-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-slate-800">Unterweisungen</h1>
              <p className="text-xs text-slate-500">Globale Vorlagen für alle SiFa-Kunden</p>
            </div>
          </div>
          {reiter === 'vorlagen' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => void neuLaden()}
                disabled={laden}
                title="Neu laden"
                aria-label="Neu laden"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-40"
              >
                <RefreshCw className={`w-4 h-4 ${laden ? 'animate-spin' : ''}`} />
              </button>
              {darfBearbeiten && (
                <button
                  onClick={() => { setAnlegenFehler(null); setNeuOffen(true); }}
                  className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-colors"
                >
                  <Plus className="w-4 h-4" /> Neue Vorlage
                </button>
              )}
            </div>
          )}
        </div>

        <div className="inline-flex flex-wrap rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" role="tablist">
          {REITER.map((r) => (
            <button
              key={r.key}
              type="button"
              role="tab"
              aria-selected={reiter === r.key}
              onClick={() => reiterWechseln(r.key)}
              className={`flex items-center gap-2 px-4 md:px-5 py-2.5 min-h-[44px] text-sm font-bold whitespace-nowrap border-b-2 transition-all ${
                reiter === r.key
                  ? 'border-teal-600 text-teal-700 bg-teal-50/50'
                  : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              {r.icon}
              {r.label}
            </button>
          ))}
        </div>

        {reiter === 'datenschutz' ? (
          <DatenschutzReiter isAdmin={isAdmin} isMobile={isMobile} onDirtyChange={setDatenschutzDirty} bueroName={bueroName} />
        ) : (
        <>
        {!darfBearbeiten && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">{KEIN_RECHT_HINWEIS}</div>
        )}

        {meldung && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${meldung.ton === 'fehler' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
            {meldung.ton === 'fehler' && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
            <span className="flex-1">{meldung.text}</span>
            <button onClick={() => setMeldung(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {fehler && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex flex-wrap items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" /><span className="flex-1 min-w-[12rem]">Die Vorlagen ließen sich nicht laden: {fehler}</span>
            <button onClick={() => void neuLaden()} disabled={laden} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-bold text-red-700 bg-white border border-red-200 hover:bg-red-50 rounded-lg disabled:opacity-40">
              <RefreshCw className="w-3.5 h-3.5" /> Erneut laden
            </button>
          </div>
        )}

        {vorlagen.length > 0 && (
          <div className="space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Titel, Kategorie, Beschreibung…"
                aria-label="Vorlagen durchsuchen"
                className="w-full pl-9 pr-3 py-2 min-h-[40px] text-sm bg-white border border-slate-200 rounded-xl outline-none focus:ring-1 focus:ring-teal-300"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {(Object.keys(STATUS_FILTER_LABEL) as VorlagenStatusFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  aria-pressed={statusFilter === f}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${statusFilter === f ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                >
                  {STATUS_FILTER_LABEL[f]} ({zaehler[f]})
                </button>
              ))}
              <span className="text-xs text-slate-500 ml-auto">{sichtbar.length} von {vorlagen.length}</span>
            </div>
          </div>
        )}

        {laden && vorlagen.length === 0 ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>
        ) : vorlagen.length === 0 ? (
          !fehler && (
            <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
              <p className="text-sm text-slate-600 font-semibold">Noch keine Vorlagen</p>
              <p className="text-xs text-slate-500 mt-1">
                {darfBearbeiten
                  ? '„Neue Vorlage" legt eine Unterweisung an, die alle SiFa-Kunden übernehmen können: Abschnitte mit Text, Bild oder Video und Fragen zur Lernerfolgskontrolle. Freigegeben steht sie beim Kunden unter „Neue Unterweisung → Aus Vorlage" zur Wahl.'
                  : 'Sobald ein Admin oder eine SiFa-Fachkraft Vorlagen anlegt, stehen sie hier.'}
              </p>
            </div>
          )
        ) : gruppen.length === 0 ? (
          <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
            <p className="text-sm text-slate-600 font-semibold">Kein Treffer</p>
            <p className="text-xs text-slate-500 mt-1">Suche oder Statusfilter ändern — Archivierte stehen unter „Archiviert" und „Alle".</p>
          </div>
        ) : (
          <div className="space-y-5">
            {gruppen.map((g) => (
              <section key={g.schluessel || '—'} className="space-y-2">
                <h2 className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">
                  {g.label} <span className="font-semibold tracking-normal">({g.vorlagen.length})</span>
                </h2>
                <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
                  {g.vorlagen.map((v) => karte(v))}
                </div>
              </section>
            ))}
          </div>
        )}
        </>
        )}
      </div>

      {testFuer && (
        <TestLinkDialog art="vorlage" id={testFuer.id} titel={testFuer.titel} onSchliessen={() => setTestFuer(null)} />
      )}

      {reiterFrage && (
        <RueckfrageDialog
          titel="Ungespeicherte Änderungen"
          text="Die Änderungen an den Datenschutzhinweisen sind nicht gespeichert. Verwerfen und den Reiter wechseln?"
          jaText="Verwerfen"
          gefaehrlich
          onNein={() => setReiterFrage(null)}
          onJa={() => { setDatenschutzDirty(false); setReiter(reiterFrage); setReiterFrage(null); }}
        />
      )}

      {neuOffen && (
        <NeueVorlageDialog
          kategorien={kategorien}
          laeuft={anlegenLaeuft}
          fehler={anlegenFehler}
          onAnlegen={(titel, kategorie) => void anlegen(titel, kategorie)}
          onAbbrechen={() => setNeuOffen(false)}
        />
      )}

      {frage && (
        <RueckfrageDialog
          titel={frage.art === 'loeschen' ? 'Vorlage löschen' : frage.art === 'freigeben' ? 'Vorlage freigeben' : 'Vorlage archivieren'}
          text={frageText}
          jaText={frage.art === 'loeschen' ? 'Löschen' : frage.art === 'freigeben' ? 'Freigeben' : 'Archivieren'}
          gefaehrlich={frage.art === 'loeschen'}
          laeuft={busyId === frage.vorlage.id}
          onNein={() => setFrage(null)}
          onJa={() => frageBestaetigt(frage)}
        />
      )}
    </div>
  );
}

/**
 * Titel (Pflicht) und Kategorie der neuen Vorlage — in der App statt
 * `window.prompt`. Danach geht es direkt in den Editor.
 */
const NeueVorlageDialog: React.FC<{
  kategorien: string[];
  laeuft: boolean;
  fehler: string | null;
  onAnlegen: (titel: string, kategorie: string) => void;
  onAbbrechen: () => void;
}> = ({ kategorien, laeuft, fehler, onAnlegen, onAbbrechen }) => {
  const [titel, setTitel] = useState('');
  const [kategorie, setKategorie] = useState('');
  const feld = 'mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 min-h-[44px] text-sm focus:outline-none focus:ring-2 focus:ring-teal-200';

  const absenden = (e: React.FormEvent) => {
    e.preventDefault();
    if (laeuft || !titel.trim()) return;
    onAnlegen(titel.trim(), kategorie.trim());
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={laeuft ? undefined : onAbbrechen}
      onKeyDown={(e) => { if (e.key === 'Escape' && !laeuft) onAbbrechen(); }}
      role="presentation"
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-label="Neue Vorlage"
        onSubmit={absenden}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">Neue Vorlage</h3>
            <p className="text-xs text-slate-500">Startet als Entwurf. Kunden sehen sie erst nach der Freigabe.</p>
          </div>
          <button type="button" onClick={onAbbrechen} disabled={laeuft} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Schließen">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <label className="block">
          <span className="text-xs font-bold text-slate-600">Titel</span>
          <input autoFocus value={titel} onChange={(e) => setTitel(e.target.value)} maxLength={200} placeholder="z. B. Allgemeine Unterweisung" className={feld} />
        </label>
        <label className="block">
          <span className="text-xs font-bold text-slate-600">Kategorie <span className="font-normal text-slate-400">(optional)</span></span>
          <input list="vorlagen-kategorien-neu" value={kategorie} onChange={(e) => setKategorie(e.target.value)} maxLength={80} placeholder="z. B. Brandschutz" className={feld} />
          <datalist id="vorlagen-kategorien-neu">
            {kategorien.map((k) => <option key={k} value={k} />)}
          </datalist>
        </label>
        {fehler && (
          <p className="text-xs text-red-700 flex items-start gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0" /> {fehler}</p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onAbbrechen} disabled={laeuft} className="min-h-[44px] px-4 text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors disabled:opacity-40">
            Abbrechen
          </button>
          <button type="submit" disabled={laeuft || !titel.trim()} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-colors disabled:opacity-40">
            {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Anlegen und bearbeiten
          </button>
        </div>
      </form>
    </div>
  );
};
